import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import xcentricService from '../services/xcentricService';
import type { XcentricWorklistItem } from '../services/xcentricService';

interface PacsStudyBannerProps {
  sessionId: string | null;
  /** Bump to re-fetch (e.g. after finalize) so delivery status stays current. */
  refreshKey?: unknown;
}

const DELIVERY: Record<string, { label: string; color: 'success' | 'error' | 'info' | 'default' }> = {
  sent: { label: 'Report delivered to Xcentic', color: 'success' },
  acknowledged: { label: 'Report delivered to Xcentic', color: 'success' },
  pending: { label: 'Sending report to Xcentic…', color: 'info' },
  failed: { label: 'Delivery to Xcentic failed — retrying', color: 'error' },
};

/**
 * Always-visible identity strip for dictation sessions handed over by Xcentic
 * PACS, so the radiologist can confirm patient and study before dictating
 * without opening the context panels. Renders nothing for local sessions.
 */
const PacsStudyBanner: React.FC<PacsStudyBannerProps> = ({ sessionId, refreshKey }) => {
  const [study, setStudy] = useState<XcentricWorklistItem | null>(null);

  useEffect(() => {
    let active = true;
    if (!sessionId) {
      setStudy(null);
      return;
    }
    xcentricService.getSessionStudy(sessionId)
      .then((s) => { if (active) setStudy(s); })
      .catch(() => { if (active) setStudy(null); });
    return () => { active = false; };
  }, [sessionId, refreshKey]);

  if (!study) return null;
  const delivery = study.writeback_status ? DELIVERY[study.writeback_status] : null;

  return (
    <Alert
      severity="info"
      icon={false}
      sx={{ mb: 1.5, alignItems: 'center', '& .MuiAlert-message': { width: '100%' } }}
      data-testid="pacs-study-banner"
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Chip size="small" color="primary" label="Xcentic PACS" />
          <Box>
            <Typography variant="subtitle2" component="span">{study.patient_name || 'Unknown patient'}</Typography>
            <Typography variant="body2" component="span" color="text.secondary">
              {' '}· MRN {study.patient_mrn || '—'} · Acc {study.accession_number || '—'}
            </Typography>
          </Box>
          <Typography variant="body2">
            {[study.modality, study.study_description].filter(Boolean).join(' · ')}
            {study.study_date ? ` · ${study.study_date}` : ''}
          </Typography>
          {study.study_priority && study.study_priority !== 'ROUTINE' && (
            <Chip size="small" color={study.study_priority === 'STAT' ? 'error' : 'warning'} label={study.study_priority} />
          )}
          {delivery && <Chip size="small" color={delivery.color} label={delivery.label} />}
        </Stack>
        {study.viewer_url && (
          <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
            href={study.viewer_url} target="_blank" rel="noopener noreferrer">
            Open images in Xcentic
          </Button>
        )}
      </Stack>
    </Alert>
  );
};

export default PacsStudyBanner;
